/**
 * Hook for fetching and managing layout data
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchLayout } from "@/api/layout";
import type { LayoutPayload } from "@/types/layout";

interface UseLayoutResult {
  layoutData: LayoutPayload | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useLayout(): UseLayoutResult {
  const [layoutData, setLayoutData] = useState<LayoutPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    // Cancel any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    // The backend serves /layout with `Cache-Control: no-store`, so the browser
    // always re-fetches fresh isUserAuthenticated state — no cache-busting needed.
    const result = await fetchLayout(controller.signal);

    if (controller.signal.aborted) return;

    if (result.ok) {
      setLayoutData(result.data);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    layoutData,
    isLoading,
    error,
    refetch: load,
  };
}
